// EOL/EOS Utility Functions - Date and Status Formatting

import { format, parseISO, differenceInDays, differenceInMonths, isBefore, addMonths } from 'date-fns';

/**
 * Format date string to display format
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = parseISO(dateString);
    return format(date, 'MMM d, yyyy');
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Get relative date description (e.g., "EOL in 3 months")
 */
export const getRelativeDate = (dateString: string | null | undefined, prefix: string = ''): string => {
  if (!dateString) return '';
  
  try {
    const date = parseISO(dateString);
    const today = new Date();
    const months = differenceInMonths(date, today);
    const days = differenceInDays(date, today);

    if (days < 0) {
      return `${prefix} ${Math.abs(months)} months ago`;
    } else if (days === 0) {
      return `${prefix} today`;
    } else if (months === 0) {
      return `${prefix} in ${days} days`;
    } else {
      return `${prefix} in ${months} months`;
    }
  } catch {
    return '';
  }
};

/**
 * Get product status based on EOL/EOS dates
 */
export const getProductStatus = (
  eolDate: string | null | undefined,
  eosDate: string | null | undefined
): 'Active' | 'EOL Soon' | 'EOS Soon' | 'Expired' => {
  const today = new Date();
  const threeMonthsFromNow = addMonths(today, 3);

  try {
    // Check EOS date first (more critical)
    if (eosDate) {
      const eos = parseISO(eosDate);
      if (isBefore(eos, today)) {
        return 'Expired';
      }
      if (isBefore(eos, threeMonthsFromNow)) {
        return 'EOS Soon';
      }
    }

    // Check EOL date
    if (eolDate) {
      const eol = parseISO(eolDate);
      if (isBefore(eol, today)) {
        return eosDate ? 'Active' : 'Expired'; // Still active if EOS hasn't passed
      }
      if (isBefore(eol, threeMonthsFromNow)) {
        return 'EOL Soon';
      }
    }

    return 'Active';
  } catch {
    return 'Active';
  }
};

/**
 * Get status color for FluentUI theme
 */
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'Active':
      return '#107C10'; // Green
    case 'EOL Soon':
      return '#F7630C'; // Orange
    case 'EOS Soon':
      return '#D83B01'; // Dark Orange/Red
    case 'Expired':
      return '#A4262C'; // Red
    default:
      return '#605E5C'; // Gray
  }
};

/**
 * Format date for API (ISO string)
 */
export const formatDateForApi = (date: Date | null | undefined): string | null => {
  if (!date) return null;
  return date.toISOString();
};

/**
 * Parse date from API response
 */
export const parseDateFromApi = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;
  try {
    return parseISO(dateString);
  } catch {
    return null;
  }
};
